import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Job } from "@modules/job/models/job.model";
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from "@angular/forms";
import { CommonService } from "@shared/services/common.service";
import { Validators } from "@config/validators.config";
import { ExtraJob } from "@modules/extra-job/models/extra-job.model";
import { SupportedCountry } from "@modules/supported-country/models/supported-country.model";
import {
  ExtraJobBillingPreviewInput,
  ExtraJobBillingPreviewResult,
  ExtraJobInput,
} from "@generated/graphql";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { InvalidControlScrollDirective } from "@shared/share-directives/directives/invalid-control-scroll.directive";
import { COMMON_SERVICE_CONFIG } from "@shared/providers/common-service.provider";
import { BusinessPlanService } from "@modules/account-settings/pages/business-profile/services/business-plan.service";
import { catchError, debounceTime, distinctUntilChanged, EMPTY, map, Observable, startWith, switchMap, tap } from "rxjs";
import { ExtraJobApi } from '@modules/extra-job/api/extra-job.api';

export interface ExtraJobDialogData {
  job: Job
  extraJob?: ExtraJob
}

export type ExtraJobForm = FormGroup<{
  id: FormControl<string | number>
  matter: FormControl<string>
  currency: FormControl<string>
  scopeOfWork: FormControl<string>
  jobId: FormControl<string>
  serviceFee: FormControl<number>
  officialFee: FormControl<number>
  outOfPocketExpenses: FormControl<number>
}>

type ExtraJobPreviewRequest = {
  input: ExtraJobBillingPreviewInput;
  signature: string;
};

@UntilDestroy()
@Component({
  selector: 'app-extra-job-dialog',
  templateUrl: './extra-job-dialog.component.html',
  styleUrls: ['./extra-job-dialog.component.scss'],
  providers: [COMMON_SERVICE_CONFIG],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExtraJobDialogComponent implements OnInit {
  static width = '50%';
  private static readonly minFeeAmount = 0;
  private static readonly minTotalAmount = 0;

  @Output() createOrUpdate = new EventEmitter<ExtraJobInput>()
  form: ExtraJobForm
  hasFormChanges = false;
  billingPreview: ExtraJobBillingPreviewResult['extraJobBillingPreview'] | null = null;
  private initialFormSignature = '';
  private _scrollEl: InvalidControlScrollDirective;

  @ViewChild(InvalidControlScrollDirective)
  set scrollEl (content: InvalidControlScrollDirective) {
    this._scrollEl = content;
  }

  get scrollEl (): InvalidControlScrollDirective {
    return this._scrollEl;
  }

  get actionLabel (): string {
    return this.extraJob ? 'Update job' : 'Create job';
  }

  get job (): Job {
    return this.data?.job
  }

  get extraJob (): ExtraJob {
    return this.data?.extraJob
  }

  get billingPreviewValidationErrorMessage(): string | null {
    const error = this.form?.getError('stripeChargeValidation') as {
      reason: string;
    } | null;

    return error?.reason ?? null;
  }

  visiblePayment$: Observable<boolean> = this.businessPlanService.ensureLoaded$().pipe(
    map((plan) => !!plan?.isPremium()),
  )

  constructor (
    public readonly commonService: CommonService,
    private readonly dialogRef: MatDialogRef<ExtraJobDialogComponent>,
    private readonly businessPlanService: BusinessPlanService,
    private readonly extraJobApi: ExtraJobApi,
    private readonly cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) private readonly data?: ExtraJobDialogData,
  ) {
    if (!this.data?.job) {
      throw new Error('Something wrong when initialize ' + ExtraJobDialogComponent.name)
    }
  }

  ngOnInit (): void {
    const {fb} = this.commonService

    this.form = fb.group({
      id: fb.control({value: null, disabled: !this.extraJob}),
      matter: fb.control(null, [Validators.required]),
      currency: fb.control(this.job.supportedCountry.currency ??
        SupportedCountry.defaultCurrency, [
        Validators.required
      ]),
      scopeOfWork: fb.control(null, [
        Validators.required
      ]),
      jobId: fb.control(this.job.id, [
        Validators.required
      ]),
      serviceFee: fb.control(ExtraJobDialogComponent.minFeeAmount, [
        Validators.required,
        Validators.min(ExtraJobDialogComponent.minFeeAmount),
      ]),
      officialFee: fb.control(ExtraJobDialogComponent.minFeeAmount, [
        Validators.required,
        Validators.min(ExtraJobDialogComponent.minFeeAmount),
      ]),
      outOfPocketExpenses: fb.control(ExtraJobDialogComponent.minFeeAmount, [
        Validators.required,
        Validators.min(ExtraJobDialogComponent.minFeeAmount),
      ]),
    }, {
      validators: [ExtraJobDialogComponent.billingTotalGreaterThanZeroValidator],
    });

    this.whenHasExtraJob()
    this.initialFormSignature = this.buildFormSignature();
    this.hasFormChanges = false;

    this.form.valueChanges.pipe(
      untilDestroyed(this),
      startWith(this.form.getRawValue()),
      tap(() => {
        this.hasFormChanges = this.buildFormSignature() !== this.initialFormSignature;
        this.cdr.markForCheck();
      }),
    ).subscribe();

    this.visiblePayment$.pipe(
      untilDestroyed(this),
      switchMap((visiblePayment) => {
        if (!visiblePayment) {
          this.clearBillingPreviewState();
          this.cdr.markForCheck();

          return EMPTY;
        }

        return this.form.valueChanges.pipe(
          startWith(this.form.getRawValue()),
          map(() => this.buildPreviewRequest()),
          tap((request) => {
            if (!request) {
              this.clearBillingPreviewState();
              this.cdr.markForCheck();
              return;
            }

            this.billingPreview = null;
            this.setFormError('billingPreviewPending', true);
            this.cdr.markForCheck();
          }),
          debounceTime(300),
          distinctUntilChanged((previous, current) => previous?.signature === current?.signature),
          switchMap((request) => {
            if (!request) {
              return EMPTY;
            }

            return this.loadBillingPreview$(request.input);
          }),
        );
      }),
    ).subscribe();
  }

  whenHasExtraJob () {
    if (this.extraJob) {
      this.form.controls.id.addValidators([Validators.required])
      this.form.patchValue(this.extraJob)
    } else {
      this.form.controls.id.removeValidators([Validators.required])
    }

    this.form.updateValueAndValidity()
  }

  onCloseDialog (): void {
    this.dialogRef.close();
  }

  onSubmit (): void {
    this.form.markAllAsTouched();

    if (this.form.invalid || !this.hasFormChanges) {
      return;
    }

    this.createOrUpdate.next(this.form.getRawValue() as ExtraJobInput)
  }

  private buildFormSignature(): string {
    const { matter, currency, scopeOfWork, serviceFee, officialFee, outOfPocketExpenses } = this.form.getRawValue();

    return [
      matter ?? '',
      currency ?? '',
      scopeOfWork ?? '',
      serviceFee ?? 0,
      officialFee ?? 0,
      outOfPocketExpenses ?? 0,
    ].join('|');
  }

  private canLoadBillingPreview(): boolean {
    return !!this.form?.controls.jobId.value
      && this.form.controls.currency.valid
      && this.form.controls.serviceFee.valid
      && this.form.controls.officialFee.valid
      && this.form.controls.outOfPocketExpenses.valid;
  }

  private buildPreviewRequest(): ExtraJobPreviewRequest | null {
    if (!this.canLoadBillingPreview()) {
      return null;
    }

    const { id, jobId, currency, serviceFee, officialFee, outOfPocketExpenses } = this.form.getRawValue();
    const input: ExtraJobBillingPreviewInput = {
      jobId,
      currency,
      serviceFee,
      officialFee,
      outOfPocketExpenses,
      ...(id ? {id: String(id)} : {}),
    };

    return {
      input,
      signature: [
        input.id ?? '',
        input.jobId,
        input.currency,
        input.serviceFee,
        input.officialFee,
        input.outOfPocketExpenses,
      ].join('|'),
    };
  }

  private loadBillingPreview$(input: ExtraJobBillingPreviewInput): Observable<ExtraJobBillingPreviewResult['extraJobBillingPreview']> {
    return this.extraJobApi.getBillingPreview$(input).pipe(
      tap((preview) => {
        if (!preview) {
          this.billingPreview = null;
          this.clearBillingPreviewErrors();
          this.setFormError('billingPreviewUnavailable', true);
          this.cdr.markForCheck();
          return;
        }

        this.billingPreview = preview;

        if (preview.requiresStripeValidation && !preview.stripeValidation.isValid) {
          this.clearBillingPreviewErrors();
          this.setFormError('stripeChargeValidation', {
            reason: preview.stripeValidation.reason || 'The Stripe charge for this billing is invalid.',
          });
        } else {
          this.clearBillingPreviewErrors();
        }

        this.cdr.markForCheck();
      }),
      catchError(() => {
        this.billingPreview = null;
        this.clearBillingPreviewErrors();
        this.setFormError('billingPreviewUnavailable', true);
        this.cdr.markForCheck();

        return EMPTY;
      }),
    );
  }

  private clearBillingPreviewState(): void {
    this.billingPreview = null;
    this.clearBillingPreviewErrors();
  }

  private clearBillingPreviewErrors(): void {
    this.clearFormError('billingPreviewPending');
    this.clearFormError('billingPreviewUnavailable');
    this.clearFormError('stripeChargeValidation');
  }

  private setFormError(key: string, value: unknown): void {
    this.form.setErrors({
      ...(this.form.errors ?? {}),
      [key]: value,
    });
  }

  private clearFormError(key: string): void {
    if (!this.form.errors?.[key]) {
      return;
    }

    const nextErrors = {...(this.form.errors ?? {})};
    delete nextErrors[key];
    this.form.setErrors(Object.keys(nextErrors).length ? nextErrors : null);
  }

  private static billingTotalGreaterThanZeroValidator(control: AbstractControl): ValidationErrors | null {
    const serviceFee = Number(control.get('serviceFee')?.value ?? 0);
    const officialFee = Number(control.get('officialFee')?.value ?? 0);
    const outOfPocketExpenses = Number(control.get('outOfPocketExpenses')?.value ?? 0);
    const total = serviceFee + officialFee + outOfPocketExpenses;

    return total > ExtraJobDialogComponent.minTotalAmount
      ? null
      : {
        billingTotalRequired: true,
      };
  }
}
